import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { RecoveryLocation } from '../../api'

export type ClockFace = 'theme' | 'refined-minimalist' | 'premium-blue' | 'frosted-glass' | 'dark-elegant'

export interface ClockSettings {
    face: ClockFace
    showSeconds: boolean
    showAnalog: boolean
}

/**
 * IANA/DST-safe zoned time. Uses Intl with the location's IANA timezone so DST is
 * automatic — never a fixed UTC offset. (en-GB/24h purely to parse numeric parts.)
 */
function getZonedTime(timeZone: string) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timeZone || 'UTC',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(new Date())
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
    let hour = get('hour')
    if (hour === 24) hour = 0 // en-GB reports midnight as 24
    return { hour, minute: get('minute'), second: get('second') }
}

const CX = 60
const CY = 60

// Hand colours per PNG face (the dial art varies light/dark). 'theme' uses CSS tokens.
const FACE_HANDS: Record<string, { hour: string; minute: string; second: string; dot: string }> = {
    'refined-minimalist': { hour: '#0f172a', minute: '#334155', second: '#dc2626', dot: '#0f172a' },
    'premium-blue': { hour: '#e2e8f0', minute: '#cbd5e1', second: '#38bdf8', dot: '#e2e8f0' },
    'frosted-glass': { hour: '#1e293b', minute: '#334155', second: '#0ea5e9', dot: '#1e293b' },
    'dark-elegant': { hour: '#f1f5f9', minute: '#cbd5e1', second: '#fbbf24', dot: '#f8fafc' },
}

/** A clock + digital readout + two summary tiles for one location. */
export function LocationClock({ location, settings }: { location: RecoveryLocation; settings: ClockSettings }) {
    const { t, i18n } = useTranslation()
    const { face, showSeconds, showAnalog } = settings
    const [, setTick] = useState(0)
    useEffect(() => {
        const id = setInterval(() => setTick((n) => (n + 1) % 86400), 1000)
        return () => clearInterval(id)
    }, [])

    const { hour, minute, second } = getZonedTime(location.timeZone)
    const hourDeg = (hour % 12) * 30 + minute * 0.5
    const minDeg = minute * 6 + second * 0.1
    const secDeg = second * 6

    // Digital readout — 12h/24h + digit formatting follow the user's locale.
    let digital: string
    try {
        digital = new Intl.DateTimeFormat(i18n.language || undefined, {
            timeZone: location.timeZone || 'UTC',
            hour: '2-digit',
            minute: '2-digit',
            ...(showSeconds ? { second: '2-digit' as const } : {}),
        }).format(new Date())
    } catch {
        const p = (n: number) => String(n).padStart(2, '0')
        digital = showSeconds ? `${p(hour)}:${p(minute)}:${p(second)}` : `${p(hour)}:${p(minute)}`
    }

    const pad = (n: number) => String(n).padStart(2, '0')
    const timeParts = showSeconds ? [pad(hour), pad(minute), pad(second)] : [pad(hour), pad(minute)]

    const nf = new Intl.NumberFormat(i18n.language || undefined)
    const ticks = Array.from({ length: 12 }, (_, i) => i * 30)
    const isTheme = face === 'theme'
    const hands = FACE_HANDS[face]

    const handProps = (kind: 'hour' | 'minute' | 'second') => {
        if (isTheme) {
            return { className: kind === 'second' ? 'stroke-primary' : 'stroke-foreground' }
        }
        return { stroke: hands ? hands[kind] : '#0f172a' }
    }

    return (
        <div className="flex w-40 shrink-0 flex-col items-center gap-1.5">
            <div className="w-full truncate text-center text-sm font-semibold text-foreground" title={location.name}>
                {location.name}
            </div>

            {showAnalog ? (
                <div className="relative h-24 w-24">
                    {!isTheme && (
                        <img
                            src={`/clock-faces/${face}.png`}
                            alt=""
                            className="absolute inset-0 h-full w-full object-contain"
                            draggable={false}
                        />
                    )}
                    <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full" role="img" aria-label={`${location.name} ${digital}`}>
                        {isTheme && (
                            <>
                                <circle cx={CX} cy={CY} r="56" className="fill-card stroke-primary/50" strokeWidth="2.5" />
                                {ticks.map((deg, i) => (
                                    <line
                                        key={i}
                                        x1={CX}
                                        y1={8}
                                        x2={CX}
                                        y2={i % 3 === 0 ? 18 : 13}
                                        className={i % 3 === 0 ? 'stroke-primary' : 'stroke-muted-foreground/60'}
                                        strokeWidth={i % 3 === 0 ? 3 : 1}
                                        strokeLinecap="round"
                                        transform={`rotate(${deg} ${CX} ${CY})`}
                                    />
                                ))}
                            </>
                        )}
                        {/* VaultSentinel shield (favicon) — small mark on every face, behind the hands */}
                        <image href="/favicon.png" x={CX - 6} y={29} width={12} height={14} preserveAspectRatio="xMidYMid meet" opacity={0.95} />
                        <line x1={CX} y1={CY + 8} x2={CX} y2={CY - 30} strokeWidth="3.5" strokeLinecap="round" transform={`rotate(${hourDeg} ${CX} ${CY})`} {...handProps('hour')} />
                        <line x1={CX} y1={CY + 10} x2={CX} y2={CY - 44} strokeWidth="2.5" strokeLinecap="round" transform={`rotate(${minDeg} ${CX} ${CY})`} {...handProps('minute')} />
                        {showSeconds && (
                            <line x1={CX} y1={CY + 12} x2={CX} y2={CY - 48} strokeWidth="1.5" strokeLinecap="round" transform={`rotate(${secDeg} ${CX} ${CY})`} {...handProps('second')} />
                        )}
                        <circle cx={CX} cy={CY} r="3.5" {...(isTheme ? { className: 'fill-foreground' } : { fill: hands ? hands.dot : '#0f172a' })} />
                    </svg>
                </div>
            ) : null}

            <div className={`font-light tabular-nums tracking-wide text-muted-foreground ${showAnalog ? 'text-lg' : 'text-4xl py-2'}`}>
                {timeParts.join(':')}
            </div>

            <div className="grid w-full grid-cols-2 gap-1.5">
                <div className="rounded-md border bg-muted/30 px-2 py-1 text-center">
                    <div className="text-sm font-semibold text-foreground">{nf.format(location.objectCount)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {t('recoverRequests.locationTracking.objects', 'Objects')}
                    </div>
                </div>
                <div className="rounded-md border bg-muted/30 px-2 py-1 text-center">
                    <div className="text-sm font-semibold text-foreground">{nf.format(location.assignedEngineerCount)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        {t('recoverRequests.locationTracking.engineers', 'Engineers')}
                    </div>
                </div>
            </div>
        </div>
    )
}
